import { useSyncExternalStore } from 'react';
import { toast } from 'react-toastify';
import { Download, Smartphone } from 'lucide-react';
import { isStandalone, subscribeInstall, triggerInstall } from '@/pwa';
import { PLATFORM_NAME } from '@/ui/PlatformLogo';

/**
 * PWA Install Button.
 *
 * Hidden when already running in standalone (installed) mode.
 * Supports different variants ('store-header', 'header', 'util', 'footer', 'default')
 * and triggers native install prompts without instruction popups.
 */
export function InstallAppButton({ variant = 'default', className = '', label = 'Download App' }) {
  const standalone = useSyncExternalStore(subscribeInstall, isStandalone);

  if (standalone) return null;

  const handleClick = async (e) => {
    e?.preventDefault?.();
    const result = await triggerInstall();

    if (result === 'accepted') {
      toast.success(`${PLATFORM_NAME} app is installing`);
    } else if (result === 'already_installed') {
      toast.info(`${PLATFORM_NAME} is already installed.`);
    }
    // Instructions suppressed per user requirement "(make sure it not showing instruction)"
  };

  if (variant === 'store-header' || variant === 'header') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`pwa-btn ${className}`}
        title={`Download ${PLATFORM_NAME} App`}
        aria-label={`Download ${PLATFORM_NAME} App`}
      >
        <Download className="i" aria-hidden="true" />
        <span className="pwa-btn-label">{label}</span>
      </button>
    );
  }

  if (variant === 'footer') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`app ${className}`}
        title={`Install ${PLATFORM_NAME} App`}
      >
        <Smartphone className="i" aria-hidden="true" />
        <span>
          <small>Fast &amp; Offline</small>
          <b>{label}</b>
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 ${className}`}
      title={`Install ${PLATFORM_NAME} App`}
    >
      <Download className="h-4 w-4" aria-hidden="true" />
      <span className="hidden sm:inline">{label}</span>
      <span className="sr-only sm:hidden">{label}</span>
    </button>
  );
}
